import { useSyncExternalStore } from 'react';

/** UI-уведомление, доставленное по SSE из bff-ui. */
export interface AppNotification {
  id: string;
  type: string;
  /** Аккаунт (serviceSubscriptionId) получателя — для переключения подписки при клике. */
  accountId?: string | null;
  mailboxId: string;
  messageId: string;
  from: string;
  subject?: string | null;
  attachmentCount: number;
  createdAt: string;
  read: boolean;
  // Детали lifecycle-уведомлений (promt-78 §C) — UI строит человекочитаемый текст (i18n).
  minutes?: number | null;
  percent?: number | null;
  usedMb?: number | null;
  limitMb?: number | null;
}

const MAX_ITEMS = 50;
const READ_STORAGE_KEY = 'k1.notifications.readIds';
const ITEMS_STORAGE_KEY = 'k1.notifications.items';
// Идентификатор владельца сохранённых уведомлений (пользователь + активная подписка).
// Если он не совпадает с текущим — хранилище считается чужим и очищается (§4).
const OWNER_STORAGE_KEY = 'k1.notifications.owner';

type Listener = () => void;

type StoredNotification = Omit<AppNotification, 'read'>;

function loadStoredItems(): StoredNotification[] {
  try {
    const raw = sessionStorage.getItem(ITEMS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredNotification[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveStoredItems(list: AppNotification[]): void {
  try {
    const payload: StoredNotification[] = list.map(({ read: _read, ...rest }) => rest);
    sessionStorage.setItem(ITEMS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
}

function loadReadIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(READ_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as string[];
    return new Set(parsed);
  } catch {
    return new Set();
  }
}

function saveReadIds(ids: Set<string>): void {
  try {
    sessionStorage.setItem(READ_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore quota */
  }
}

// Стартуем с пустого состояния: сохранённые уведомления восстанавливаются только после
// подтверждения владельца в setNotificationOwner (чтобы не показывать чужие/устаревшие).
let readIds = new Set<string>();

function withReadState(n: Omit<AppNotification, 'read'>): AppNotification {
  return { ...n, read: readIds.has(n.id) };
}

let items: AppNotification[] = [];
let currentOwner: string | null | undefined;
const listeners = new Set<Listener>();

function emit() {
  items = [...items];
  saveStoredItems(items);
  listeners.forEach((l) => {
    l();
  });
}

/** Подгружает историю с сервера (GET /api/notifications) при старте сессии. */
export function hydrateNotifications(list: Omit<AppNotification, 'read'>[]): void {
  if (list.length === 0) return;
  const byId = new Map(items.map((x) => [x.id, x]));
  for (const n of list) {
    if (!byId.has(n.id)) byId.set(n.id, withReadState(n));
  }
  items = [...byId.values()]
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .slice(0, MAX_ITEMS);
  emit();
}

/** Добавляет уведомление в начало списка (дедуп по id), обрезает до MAX_ITEMS. */
export function addNotification(n: Omit<AppNotification, 'read'>): void {
  if (items.some((x) => x.id === n.id)) return;
  items = [withReadState(n), ...items].slice(0, MAX_ITEMS);
  emit();
}

export function markAllRead(): void {
  if (!items.some((x) => !x.read)) return;
  for (const n of items) {
    if (!n.read) readIds.add(n.id);
  }
  saveReadIds(readIds);
  items = items.map((x) => (x.read ? x : { ...x, read: true }));
  emit();
}

export function clearNotifications(): void {
  if (items.length === 0) return;
  for (const n of items) readIds.add(n.id);
  saveReadIds(readIds);
  items = [];
  try {
    sessionStorage.removeItem(ITEMS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  emit();
}

/**
 * Синхронизирует стор с текущим владельцем (пользователь + активная подписка).
 * - Совпадает с сохранённым → восстанавливает историю из sessionStorage.
 * - Отличается или владельца нет (выход) → очищает хранилище и in-memory состояние.
 * Так уведомления не «протекают» между разными пользователями/подписками (§4).
 */
export function setNotificationOwner(owner: string | null): void {
  if (owner === currentOwner) return;
  currentOwner = owner;

  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(OWNER_STORAGE_KEY);
  } catch {
    /* ignore */
  }

  if (owner !== null && stored === owner) {
    // Тот же пользователь — подмешиваем сохранённую историю к уведомлениям текущей сессии
    // (последние, доставленные по SSE до восстановления, не теряются).
    readIds = loadReadIds();
    const byId = new Map(items.map((x) => [x.id, x]));
    for (const n of loadStoredItems()) {
      if (!byId.has(n.id)) byId.set(n.id, withReadState(n));
    }
    items = [...byId.values()]
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
      .slice(0, MAX_ITEMS);
    emit();
    return;
  }

  // Другой пользователь (или выход): отбрасываем ЧУЖУЮ сохранённую историю и её read-метки,
  // но НЕ трогаем уведомления, доставленные в текущей сессии, — они принадлежат текущему
  // пользователю. emit() ниже перезапишет хранилище уже «своими» уведомлениями.
  readIds = new Set();
  try {
    sessionStorage.removeItem(READ_STORAGE_KEY);
    if (owner !== null) sessionStorage.setItem(OWNER_STORAGE_KEY, owner);
    else sessionStorage.removeItem(OWNER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  emit();
}

function subscribe(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function useNotifications(): AppNotification[] {
  return useSyncExternalStore(
    subscribe,
    () => items,
    () => items,
  );
}

export function useUnreadCount(): number {
  const list = useNotifications();
  return list.reduce((acc, n) => (n.read ? acc : acc + 1), 0);
}
